<template>
  <div class="h5-container chat-page">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      class="top-bar"
      :title="serviceName || '在线客服'"
      left-text="返回"
      left-arrow
      @click-left="goBack"
    >
      <template #right>
        <span class="status" :class="{ online: wsConnected }">{{ wsConnected ? '在线' : '离线' }}</span>
      </template>
    </van-nav-bar>

    <!-- 消息列表 -->
    <div class="chat-body" ref="chatBody">
      <!-- 加载更多 -->
      <div class="load-more" v-if="hasMore" @click="loadMore">
        <span>{{ loading ? '加载中...' : '加载更多历史消息' }}</span>
      </div>

      <template v-for="(msg, index) in messageList">
        <!-- 微信风格时间分隔：间隔超过5分钟才显示 -->
        <div class="time-divider" v-if="shouldShowTime(index)">
          <span>{{ formatChatTime(msg.createTime) }}</span>
        </div>
        <div class="msg-item" :key="index" :class="String(msg.fromId) === String(userId) ? 'mine' : 'other'">
          <div class="avatar other-avatar" v-if="String(msg.fromId) !== String(userId)">
            <van-icon name="service" size="18" color="#D4AF37" />
          </div>
          <div class="msg-content">
            <div class="sender-name">{{ String(msg.fromId) === String(userId) ? '我' : (msg.fromNickname || '客服') }}</div>
            <div class="bubble" :class="'bubble-' + (msg.contentType || 'text')">
              <template v-if="!msg.contentType || msg.contentType === 'text'">{{ msg.content }}</template>
              <img v-else-if="msg.contentType === 'image'" :src="msg.content" class="msg-image" @click="previewImage(msg.content)" />
              <video v-else-if="msg.contentType === 'video'" :src="msg.content" class="msg-video" controls />
            </div>
          </div>
          <div class="avatar mine-avatar" v-if="String(msg.fromId) === String(userId)">
            <van-icon name="user" size="18" color="#fff" />
          </div>
        </div>
      </template>
    </div>

    <!-- 输入区域 -->
    <div class="chat-footer">
      <input ref="imageInput" type="file" accept="image/*" style="display:none" @change="handleUploadImage" />
      <input ref="videoInput" type="file" accept="video/*" style="display:none" @change="handleUploadVideo" />
      <div class="upload-row">
        <button class="upload-btn" @click="$refs.imageInput.click()" :disabled="uploading">
          <van-icon name="photo" size="16" color="#D4AF37" /><span>图片</span>
        </button>
        <button class="upload-btn" @click="$refs.videoInput.click()" :disabled="uploading">
          <van-icon name="video" size="16" color="#D4AF37" /><span>视频</span>
        </button>
      </div>
      <div class="input-row">
        <input
          v-model="inputText"
          class="msg-input"
          placeholder="请输入消息..."
          @keyup.enter="sendMessage"
        />
        <button class="send-btn" @click="sendMessage" :disabled="!inputText.trim()">发送</button>
      </div>
    </div>

    <!-- 图片预览 -->
    <div class="image-preview" v-if="imagePreviewVisible" @click="imagePreviewVisible = false">
      <img :src="imagePreviewUrl" class="preview-img" />
    </div>
  </div>
</template>

<script>
import { memberInformation } from '@/api/mine'
import { enterService, getHistoryMessages, markConversationRead, uploadChatFile } from '@/api/chat'

export default {
  name: 'Chat',
  data() {
    return {
      userId: '',
      userType: 2,
      serviceId: '',
      serviceName: '',
      serviceAvatar: '',
      targetUserType: 1, // 对方用户类型，从enterService返回，不一定是1
      conversationKey: '',
      ws: null,
      wsConnected: false, // WebSocket实际连接状态
      heartbeatTimer: null,
      reconnectTimer: null,
      reconnectCount: 0,
      messageList: [],
      pendingMessages: [], // 待发送消息缓存（连接没好时先存这里）
      inputText: '',
      pageNum: 1,
      pageSize: 20,
      hasMore: true,
      loading: false,
      inited: false, // 是否已完成初始化（拿到客服信息+加载历史）
      uploading: false,
      imagePreviewVisible: false,
      imagePreviewUrl: ''
    }
  },
  created() {
    this.init()
    // 页面可见性变化：切回聊天页时补标记已读
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  mounted() {
    this.updateViewportHeight()
    // 延迟一帧再计算，确保地址栏收起后获取正确高度
    requestAnimationFrame(() => this.updateViewportHeight())
    setTimeout(() => this.updateViewportHeight(), 300)
    window.addEventListener('resize', this.updateViewportHeight)
    window.addEventListener('orientationchange', this.updateViewportHeight)
  },
  activated() {
    // keep-alive重新激活时，用延迟确保获取正确高度
    this.updateViewportHeight()
    requestAnimationFrame(() => {
      this.updateViewportHeight()
      this.$nextTick(() => this.scrollToBottom())
    })
    setTimeout(() => this.updateViewportHeight(), 300)
  },
  beforeDestroy() {
    this.destroyWebSocket()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('resize', this.updateViewportHeight)
    window.removeEventListener('orientationchange', this.updateViewportHeight)
  },
  methods: {
    updateViewportHeight() {
      document.documentElement.style.setProperty('--chat-height', `${window.innerHeight - 50}px`)
    },
    async init() {
      await this.getUserInfo()
      if (!this.userId) return
      // 静默进入会话，失败也不提示，用默认值
      await this.enterServiceSession()
      // 建立连接（静默重连，不提示）
      this.connectWebSocket()
      // 加载历史消息
      this.loadHistory()
      this.inited = true
    },
    async getUserInfo() {
      try {
        const res = await memberInformation()
        if (res.code === 200 && res.data) {
          this.userId = String(res.data.id || res.data.userId || '')
        }
      } catch (e) {
        console.error('获取用户信息失败', e)
      }
    },
    async enterServiceSession() {
      try {
        const res = await enterService(this.userId)
        if (res.code === 200 && res.data) {
          const data = res.data
          this.conversationKey = data.conversationKey
          this.serviceId = data.serviceId
          this.serviceName = data.serviceNickName || '在线客服'
          this.serviceAvatar = data.serviceAvatar
          this.targetUserType = data.toUserType || 1 // 从接口获取对方用户类型，不写死
          // 存到本地，消息列表页用来查历史消息
          localStorage.setItem('chat_conversation_key', this.conversationKey)
        }
      } catch (e) {
        console.error('进入客服会话失败', e)
        // 失败也不弹提示，用默认值，连接建立后照样能聊
      }
    },
    connectWebSocket() {
      const wsUrl = this.buildWsUrl()
      try {
        this.ws = new WebSocket(`${wsUrl}/ws/chat/${this.userId}/${this.userType}`)

        this.ws.onopen = () => {
          this.wsConnected = true
          this.reconnectCount = 0
          this.startHeartbeat()
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
          }
          // 连接成功后，补发缓存的待发送消息
          this.flushPendingMessages()
        }

        this.ws.onmessage = (event) => {
          const msg = JSON.parse(event.data)
          if (msg.msgType === 'heartbeat') return
          // 转内勤系统消息：自动切换当前客服为平台内勤（conversationKey不变，历史连续）
          if (msg.msgType === 'system' && msg.content && msg.content.indexOf('转接平台客服') > -1) {
            this.serviceId = msg.fromId
            this.targetUserType = msg.fromUserType
            this.serviceName = msg.fromNickname || '平台客服'
            // 重新同步客服信息（双重保险，防止系统消息推送丢失导致发错人）
            this.enterServiceSession()
          }
          // 结束对话系统消息：重新分配新客服（用户不退出页面也能自动切换）
          if (msg.msgType === 'system' && msg.content && 
              (msg.content.indexOf('对话已结束') > -1 || msg.content.indexOf('本次对话已结束') > -1)) {
            this.enterServiceSession()
          }
          // 已读回执：对方读了我发的消息，更新本地状态，不插入列表
          if (msg.msgType === 'readReceipt') {
            this.handleReadReceipt(msg)
            return
          }
          // 普通消息/系统消息
          // 空消息过滤（防止空气泡）
          if (!msg.content || !msg.content.trim()) {
            return
          }
          // 消息去重：根据 id 或 content+fromId+createTime 防止重复回显
          const isDuplicate = this.messageList.some(m => {
            if (msg.id && m.id === msg.id) return true
            if (!msg.id && m.content === msg.content && String(m.fromId) === String(msg.fromId) && 
                new Date(m.createTime).getTime() === new Date(msg.createTime).getTime()) return true
            return false
          })
          if (isDuplicate) {
            return
          }
          this.messageList.push(msg)
          this.$nextTick(() => this.scrollToBottom())
          this.saveLastMessage(msg)
          // 只有普通消息且页面可见才标记已读（系统消息不标记）
          if (msg.msgType !== 'system' && !document.hidden) {
            msg.isRead = 1
            this.markRead()
          }
        }

        this.ws.onclose = () => {
          this.wsConnected = false
          this.stopHeartbeat()
          // 静默自动重连，不弹任何提示
          this.autoReconnect()
        }

        this.ws.onerror = () => {
          // 错误也静默处理，交给onclose去重连
        }
      } catch (e) {
        this.wsConnected = false
        this.autoReconnect()
      }
    },
    buildWsUrl() {
      // 开发环境：用当前页面的主机名 + 后端端口，支持 localhost 和局域网IP
      if (process.env.NODE_ENV === 'development') {
        return `ws://${window.location.hostname}:8080`
      }
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      return `${protocol}//${window.location.host}`
    },
    startHeartbeat() {
      this.heartbeatTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ msgType: 'heartbeat' }))
        }
      }, 30000)
    },
    stopHeartbeat() {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer)
        this.heartbeatTimer = null
      }
    },
    autoReconnect() {
      if (this.reconnectTimer) return
      // 重连间隔递增，最多30秒，避免疯狂重连
      this.reconnectCount++
      const delay = Math.min(this.reconnectCount * 3000, 30000)
      this.reconnectTimer = setTimeout(() => {
        this.connectWebSocket()
      }, delay)
    },
    destroyWebSocket() {
      this.stopHeartbeat()
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }
    },
    // 发送消息：连接没好就缓存，不弹提示
    sendMessage() {
      const content = this.inputText.trim()
      if (!content) return

      const msg = {
        toId: this.serviceId,
        toUserType: this.targetUserType,
        content: content,
        contentType: 'text',
        msgType: 'chat',
        conversationKey: this.conversationKey
      }

      // 本地先渲染，用户立刻能看到自己发的消息
      const localMsg = {
        fromId: this.userId,
        fromUserType: this.userType,
        toId: this.serviceId,
        toUserType: this.targetUserType,
        content: content,
        contentType: 'text',
        msgType: 'chat',
        conversationKey: this.conversationKey,
        isRead: 0,
        createTime: new Date()
      }
      this.messageList.push(localMsg)
      this.inputText = ''
      this.$nextTick(() => this.scrollToBottom())
      this.saveLastMessage(localMsg)

      if (this.wsConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        // 连接正常，直接发
        this.ws.send(JSON.stringify(msg))
      } else {
        // 连接没好，先缓存，连接成功后自动补发
        this.pendingMessages.push(msg)
      }
    },
    // 补发缓存的消息
    flushPendingMessages() {
      if (this.pendingMessages.length === 0) return
      this.pendingMessages.forEach(msg => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify(msg))
        }
      })
      this.pendingMessages = []
    },
    async loadHistory() {
      if (!this.conversationKey) return
      this.loading = true
      try {
        const res = await getHistoryMessages(this.conversationKey, this.pageNum, this.pageSize)
        if (res.code === 200) {
          const list = res.data || []
          const sorted = list.reverse()
          if (this.pageNum > 1) {
            this.messageList = [...sorted, ...this.messageList]
          } else {
            this.messageList = sorted
          }
          this.hasMore = list.length >= this.pageSize
          this.$nextTick(() => this.scrollToBottom())
          this.markRead()
        }
      } catch (e) {
        console.error('加载历史消息失败', e)
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      if (!this.hasMore || this.loading) return
      this.pageNum++
      this.loadHistory()
    },
    // 处理已读回执：对方读了我发的消息，更新本地状态
    handleReadReceipt(receipt) {
      if (!receipt || receipt.conversationKey !== this.conversationKey) return
      this.messageList.forEach(msg => {
        if (String(msg.fromId) === String(this.userId)) {
          msg.isRead = 1
        }
      })
    },
    // 页面可见性变化：切回聊天页时，把未读的对方消息标记为已读
    handleVisibilityChange() {
      if (!document.hidden) {
        // 页面变为可见，标记所有对方未读消息为已读
        let hasUnread = false
        this.messageList.forEach(msg => {
          if (String(msg.fromId) !== String(this.userId) && msg.isRead !== 1) {
            msg.isRead = 1
            hasUnread = true
          }
        })
        if (hasUnread) {
          this.markRead()
        }
      }
    },
    async markRead() {
      if (!this.conversationKey || !this.userId) return
      try {
        await markConversationRead(this.conversationKey, this.userId, this.userType)
        // 标记成功后，本地把对方发的消息都设为已读
        this.messageList.forEach(msg => {
          if (String(msg.fromId) !== String(this.userId)) {
            msg.isRead = 1
          }
        })
      } catch (e) {
        // 标记已读失败静默处理
      }
    },
    saveLastMessage(msg) {
      localStorage.setItem('chat_last_message', JSON.stringify(msg))
    },
    scrollToBottom() {
      const body = this.$refs.chatBody
      if (body) {
        body.scrollTop = body.scrollHeight
      }
    },
    // 判断是否需要显示时间分隔（微信风格：间隔超过5分钟才显示）
    shouldShowTime(index) {
      if (index === 0) return true // 第一条消息总是显示时间
      const current = new Date(this.messageList[index].createTime).getTime()
      const prev = new Date(this.messageList[index - 1].createTime).getTime()
      return current - prev > 5 * 60 * 1000 // 间隔超过5分钟
    },
    // 微信风格时间格式化
    formatChatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const yesterday = new Date(now.getTime() - 86400000)
      const h = String(date.getHours()).padStart(2, '0')
      const m = String(date.getMinutes()).padStart(2, '0')
      if (date.toDateString() === now.toDateString()) {
        return `${h}:${m}`
      } else if (date.toDateString() === yesterday.toDateString()) {
        return `昨天 ${h}:${m}`
      } else if (date.getFullYear() === now.getFullYear()) {
        return `${date.getMonth() + 1}月${date.getDate()}日 ${h}:${m}`
      } else {
        return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${h}:${m}`
      }
    },
    // 上传图片
    async handleUploadImage(e) {
      const file = e.target.files[0]
      if (!file) return
      await this.uploadAndSend(file, 'image')
      e.target.value = ''
    },

    // 上传视频
    async handleUploadVideo(e) {
      const file = e.target.files[0]
      if (!file) return
      await this.uploadAndSend(file, 'video')
      e.target.value = ''
    },

    // 上传并发送文件消息
    async uploadAndSend(file, contentType) {
      this.uploading = true
      try {
        const res = await uploadChatFile(file)
        if (res.code === 200) {
          const url = res.url || res.data.url
          this.sendFileMessage(url, contentType)
        } else {
          this.$toast(res.msg || '上传失败')
        }
      } catch (err) {
        console.error('上传失败', err)
        this.$toast('上传失败')
      } finally {
        this.uploading = false
      }
    },

    // 发送文件消息
    sendFileMessage(url, contentType) {
      const msg = {
        toId: this.serviceId,
        toUserType: this.targetUserType,
        content: url,
        contentType: contentType,
        msgType: 'chat',
        conversationKey: this.conversationKey
      }
      const localMsg = {
        fromId: this.userId,
        fromUserType: this.userType,
        toId: this.serviceId,
        toUserType: this.targetUserType,
        content: url,
        contentType: contentType,
        msgType: 'chat',
        conversationKey: this.conversationKey,
        isRead: 0,
        createTime: new Date()
      }
      this.messageList.push(localMsg)
      this.$nextTick(() => this.scrollToBottom())
      if (this.wsConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(msg))
      } else {
        this.pendingMessages.push(msg)
      }
    },

    // 预览图片
    previewImage(url) {
      this.imagePreviewUrl = url
      this.imagePreviewVisible = true
    },

    goBack() {
      this.$router.replace('/app_message')
      // this.$router.back()
    }
  }
}
</script>

<style scoped lang="scss">
.h5-container.chat-page {
  background-color: #1A1A1A;
  display: flex;
  flex-direction: column;
  height: var(--chat-height, 100dvh); // JS动态设置，已经减去tabbar高度
  overflow: hidden; // 禁止页面级滚动
  box-sizing: border-box;
}

.top-bar {
  background: linear-gradient(135deg, #D4AF37 0%, #B8860B 100%);
  flex-shrink: 0; // 顶部固定高度

  ::v-deep .van-nav-bar__title {
    color: #0f0f0f;
    font-weight: 600;
  }
  ::v-deep .van-nav-bar__text {
    color: #0f0f0f;
  }
  ::v-deep .van-icon-arrow-left {
    color: #0f0f0f;
  }

  .status {
    font-size: 12px;
    color: #555;
    &.online {
      color: #52c41a;
    }
  }
}

.chat-body {
  flex: 1; // 自适应填满剩余空间
  overflow-y: auto;
  padding: 12px;
  padding-bottom: 140px; // 留出输入区(约100px) + tabbar(44px)空间
  background: #1A1A1A;
  min-height: 0; // 允许flex子项收缩触发内部滚动
  -webkit-overflow-scrolling: touch; // iOS平滑滚动

  .time-divider {
    text-align: center;
    margin: 12px 0;

    span {
      display: inline-block;
      padding: 2px 10px;
      font-size: 11px;
      color: #888;
      background: rgba(255, 255, 255, 0.06);
      border-radius: 4px;
    }
  }

  .load-more {
    text-align: center;
    padding: 10px;
    font-size: 12px;
    color: #666;
    cursor: pointer;
  }

  .msg-item {
    display: flex;
    margin-bottom: 14px;
    align-items: flex-start;

    &.mine {
      justify-content: flex-end;
    }

    .avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin: 0 8px;
    }

    .other-avatar {
      background: #2a2a2a;
      border: 1px solid #D4AF37;
    }

    .mine-avatar {
      background: linear-gradient(135deg, #D4AF37 0%, #B8860B 100%);
    }

    .msg-content {
      display: flex;
      flex-direction: column;
      max-width: 70%;
      align-items: flex-start;
    }

    .sender-name {
      font-size: 11px;
      color: #888;
      margin-bottom: 3px;
      padding: 0 2px;
    }

    .bubble {
      max-width: 100%;
      padding: 9px 13px;
      border-radius: 8px;
      font-size: 14px;
      line-height: 1.5;
      word-break: break-all;
    }

    &.other .bubble {
      background: #2a2a2a;
      color: #e0e0e0;
    }

    &.mine .msg-content {
      align-items: flex-end;
    }

    &.mine .bubble {
      background: linear-gradient(135deg, #D4AF37 0%, #B8860B 100%);
      color: #0f0f0f;
    }

    /* 图片/视频消息 */
    .bubble-image,
    .bubble-video {
      padding: 0 !important;
      background: transparent !important;
      max-width: 60% !important;
      overflow: hidden;
      border-radius: 8px;
    }

    .msg-image {
      max-width: 180px;
      max-height: 180px;
      border-radius: 8px;
      display: block;
      cursor: pointer;
    }

    .msg-video {
      max-width: 220px;
      max-height: 220px;
      border-radius: 8px;
      display: block;
    }
  }
}

/* 图片预览 */
.image-preview {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;

  .preview-img {
    max-width: 95vw;
    max-height: 95vh;
    object-fit: contain;
  }
}

.chat-footer {
  position: fixed; // 固定定位，不参与flex布局
  left: 0;
  right: 0;
  bottom: 44px; // tabbar高度
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  background: #1f1f1f;
  border-top: 1px solid rgba(212, 175, 55, 0.12);
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.4);
  z-index: 100;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  gap: 8px;

  .upload-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .input-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .upload-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border: 1px solid rgba(212, 175, 55, 0.25);
    background: #262626;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #D4AF37;
    font-size: 12px;

    &:active {
      transform: scale(0.92);
      background: #2e2e2e;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .msg-input {
    flex: 1;
    min-width: 0;
    height: 38px;
    border: 1px solid #333;
    border-radius: 19px;
    padding: 0 16px;
    font-size: 14px;
    outline: none;
    background: #1A1A1A;
    color: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &::placeholder {
      color: #666;
    }

    &:focus {
      border-color: rgba(212, 175, 55, 0.6);
      box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.12);
    }
  }

  .send-btn {
    height: 38px;
    padding: 0 20px;
    background: linear-gradient(135deg, #D4AF37 0%, #B8860B 100%);
    color: #0f0f0f;
    border: none;
    border-radius: 19px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 1px;
    cursor: pointer;
    flex-shrink: 0;
    transition: transform 0.15s ease;

    &:active {
      transform: scale(0.96);
    }

    &:disabled {
      background: #3a3a3a;
      color: #777;
      cursor: not-allowed;
      box-shadow: none;
    }
  }
}

/* PC适配：参考 home.vue / mine.vue 约定，放大头像、气泡、输入区 */
@media screen and (min-width: 768px) {
  .chat-page {
    align-items: stretch; // 覆盖全局.h5-container的align-items:center，让子元素撑满宽度
    justify-content: flex-start; // 覆盖全局的justify-content:center
    height: calc(100vh - 80px); // PC端tabbar更高（80px）
    min-height: calc(100vh - 80px);
    padding-bottom: 80px; // PC端留出更多tabbar空间
    box-shadow: 0 0 24px rgba(0, 0, 0, 0.5);
  }

  .top-bar {
    height: 70px; // PC端nav-bar更高
  }

  .chat-body {
    padding: 16px;

    .msg-item {
      margin-bottom: 18px;

      .avatar {
        width: 48px;
        height: 48px;
      }

      .msg-content {
        max-width: 400px;
      }

      .bubble {
        font-size: 16px;
        padding: 12px 16px;
      }

      .msg-image {
        max-width: 240px;
        max-height: 240px;
      }

      .msg-video {
        max-width: 320px;
        max-height: 320px;
      }
    }
  }

  .chat-footer {
    flex-shrink: 0;
    padding: 14px 16px;
    padding-bottom: 14px; // PC端无需安全区
    gap: 12px;

    .upload-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border: 1px solid rgba(212, 175, 55, 0.25);
      background: #262626;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
      color: #D4AF37;
      font-size: 12px;

      &:active {
        transform: scale(0.92);
        background: #2e2e2e;
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }

    .msg-input {
      height: 48px;
      border-radius: 24px;
      font-size: 16px;
      padding: 0 20px;
    }

    .send-btn {
      height: 48px;
      padding: 0 28px;
      border-radius: 24px;
      font-size: 16px;
    }
  }
}
</style>
